import React, { FC } from "react";
import { ThemeProvider, StyledEngineProvider } from "@mui/material/styles";
import { Typography, CircularProgress } from "@mui/material";
import { Theme } from "@mui/material/styles";
import makeStyles from "@mui/styles/makeStyles";
import { useParams } from "react-router-dom";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import { useBranchedStory } from "../hooks/useBranchedStory";
import { BranchedStoryAccordionView } from "../components/BranchedStoryAccordionView";
import { BranchedStoryProvider } from "../state/BranchedStoryState";
import { VCard } from "../../../../core/components/VCard";
import { verbalTheme } from "../../../layout/themes/verbal.theme";

declare module "@mui/styles/defaultTheme" {
    // eslint-disable-next-line @typescript-eslint/no-empty-interface
    interface DefaultTheme extends Theme {}
}

// Styles following design guide patterns
const useStyles = makeStyles((theme: Theme) => ({
    container: {
        padding: theme.spacing(3), // 24px
        height: "calc(100vh - 200px)",
        display: "flex",
        flexDirection: "column",
    },
    headerCard: {
        marginBottom: theme.spacing(2), // 16px
        padding: theme.spacing(2), // 16px
    },
    headerContent: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        flexWrap: "wrap",
    },
    title: {
        display: "flex",
        alignItems: "center",
        fontWeight: "bold",
        "& > svg": {
            marginRight: theme.spacing(1),
        },
    },
    structureContainer: {
        flex: 1,
        position: "relative",
        overflow: "hidden",
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.shape.borderRadius, // 8px
    },
}));

// Structure content component
const StructureContent: FC<{ storyId: number }> = ({ storyId }) => {
    const classes = useStyles();
    const { structure, loading, error } = useBranchedStory(storyId);

    if (loading || error || !structure) {
        return (
            <>
                {loading ? (
                    <CircularProgress />
                ) : error ? (
                    <Typography color="error">{error}</Typography>
                ) : (
                    !structure && <Typography color="error">Story structure not found or invalid</Typography>
                )}
            </>
        );
    }

    return (
        <>
            {/* Header */}
            <VCard className={classes.headerCard}>
                <div className={classes.headerContent}>
                    <Typography variant="h5" className={classes.title}>
                        <AccountTreeIcon />
                        {structure.title || "Story Structure"}
                    </Typography>
                </div>

                <Typography variant="body2" color="textSecondary">
                    Story structure view • {structure.totalParts} parts • Expand sections to view details
                </Typography>
            </VCard>

            {/* Structure Visualization - Accordion View */}
            <div className={classes.structureContainer}>
                <div style={{ padding: "16px", height: "100%", overflow: "auto" }}>
                    <BranchedStoryAccordionView parts={structure.allParts || []} />
                </div>
            </div>
        </>
    );
};

/**
 * BranchedStoryStructureScreen Component
 *
 * Full-screen structure visualization for branched stories.
 * Provides accordion-based navigation through the story structure.
 *
 * Features:
 * - Accordion-based structure view
 * - View story parts with decision points and questions
 * - Collapsible sections for organized viewing
 * - View-only mode (no editing)
 *
 * @example
 * <BranchedStoryStructureScreen />
 */
export const BranchedStoryStructureScreen: FC = () => {
    const classes = useStyles();
    const params = useParams();
    const id = params.id!;
    const storyId = parseInt(id || "0", 10);

    return (
        <StyledEngineProvider injectFirst>
            (
            <ThemeProvider theme={verbalTheme}>
                <BranchedStoryProvider>
                    <div className={classes.container}>
                        <StructureContent storyId={storyId} />
                    </div>
                </BranchedStoryProvider>
            </ThemeProvider>
            )
        </StyledEngineProvider>
    );
};

// Display name for debugging (following design guide)
BranchedStoryStructureScreen.displayName = "BranchedStoryStructureScreen";
